import { OpenFeature, ProviderEvents } from '@openfeature/web-sdk';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';
import { OpenFeatureSplitProvider } from '@splitsoftware/openfeature-web-split-provider';
import appConfig from 'appConfig';
import featureKeys from './featureKeys';

const LD_ROUTING_FLAG_KEY = featureKeys.SERVE_FROM_LAUNCHDARKLY;

/** Split returns this when the flag name is not defined in the environment (or SDK not ready). */
const SPLIT_UNDEFINED_TREATMENT = 'control';

/** LD → Harness (Split): `.` → `-`; keys with only `_`/`-` pass through unchanged. */
export function defaultHarnessKeyFromLdKey(ldKey) {
  return ldKey.replace(/\./g, '-');
}

/**
 * Harness FME (Split / OpenFeature) controller: maps Harness evaluations onto LaunchDarkly-style keys
 * in the shared `featureFlags` object supplied via `getFeatureFlags`.
 *
 * When Harness is active (`serve-from-launchDarkly` is not strictly `true` in LD):
 * - Each boolean in the LD snapshot is overlaid using `defaultHarnessKeyFromLdKey(ldKey)` as the Split flag key.
 * - If Split has no treatment, `getBooleanValue` keeps the LD baseline. `is()` still uses the LD key string.
 *
 * @param {{ getFeatureFlags: () => Record<string, unknown>, notifyListeners: (flag: string) => void }} deps
 */
export function createHarness({ getFeatureFlags, notifyListeners }) {
  let splitFactory = null;
  let ofClient = null;
  let configurationChangedHandler = null;

  function snapshotFeatureFlagsShallow() {
    return { ...getFeatureFlags() };
  }

  function notifyListenersForChangedKeys(prevByKey) {
    const featureFlags = getFeatureFlags();
    const keys = new Set([...Object.keys(prevByKey), ...Object.keys(featureFlags)]);
    keys.forEach((k) => {
      if (featureFlags[k] !== prevByKey[k]) {
        notifyListeners(k);
      }
    });
  }

  async function teardownHarness() {
    if (configurationChangedHandler) {
      OpenFeature.removeHandler(
        ProviderEvents.ConfigurationChanged,
        configurationChangedHandler
      );
      configurationChangedHandler = null;
    }
    if (splitFactory) {
      await splitFactory.destroy();
      splitFactory = null;
    }
    ofClient = null;
  }

  /** Overwrite boolean LD snapshot values with Split booleans (`defaultHarnessKeyFromLdKey`). */
  function applyHarnessOverlay() {
    if (!ofClient) return;
    const featureFlags = getFeatureFlags();

    Object.keys(featureFlags).forEach((ldKey) => {
      if (ldKey === LD_ROUTING_FLAG_KEY) {
        return;
      }
      const ldVal = featureFlags[ldKey];
      if (typeof ldVal === 'boolean') {
        const harnessKey = defaultHarnessKeyFromLdKey(ldKey);
        featureFlags[ldKey] = ofClient.getBooleanValue(harnessKey, ldVal);
      }
    });
  }

  function reapplyHarnessOverlayAndNotify() {
    if (!ofClient) return;
    const prev = snapshotFeatureFlagsShallow();
    applyHarnessOverlay();
    notifyListenersForChangedKeys(prev);
  }

  /**
   * Lazy `is()` lookup for LD-style keys not in `featureFlags`.
   * @returns {null} Harness not initialized
   * @returns {{ inHarness: false }} No Split flag for the resolved Harness key (`getTreatment` is `control`)
   * @returns {{ inHarness: true, value: boolean }} Flag exists in Harness; value from OpenFeature
   */
  function getHarnessBooleanForLdStyleKey(ldKey) {
    if (!ofClient || !splitFactory) {
      return null;
    }
    if (ldKey === LD_ROUTING_FLAG_KEY) {
      return null;
    }
    const harnessKey = defaultHarnessKeyFromLdKey(ldKey);
    const treatment = splitFactory.client().getTreatment(harnessKey);
    if (treatment === SPLIT_UNDEFINED_TREATMENT) {
      return { inHarness: false };
    }
    return { inHarness: true, value: ofClient.getBooleanValue(harnessKey, false) };
  }

  async function setupHarness(user) {
    let targetingKey = 'anonymous';
    if (user && user.accountId) {
      targetingKey = user.accountId;
    }

    splitFactory = SplitFactory({
      core: {
        authorizationKey: appConfig.getHarnessFMESDKKey(),
        key: targetingKey,
      },
    });

    const provider = new OpenFeatureSplitProvider(splitFactory);
    await OpenFeature.setProviderAndWait(provider);

    const context = { targetingKey };
    if (user) {
      context.firstName = user.givenName || '';
      context.lastName = user.familyName || '';
    }
    await OpenFeature.setContext(context);

    ofClient = OpenFeature.getClient();
    applyHarnessOverlay();

    if (configurationChangedHandler) {
      OpenFeature.removeHandler(
        ProviderEvents.ConfigurationChanged,
        configurationChangedHandler
      );
    }

    configurationChangedHandler = () => {
      reapplyHarnessOverlayAndNotify();
    };

    OpenFeature.addHandler(
      ProviderEvents.ConfigurationChanged,
      configurationChangedHandler
    );
  }

  return {
    teardownHarness,
    setupHarness,
    reapplyHarnessOverlayAndNotify,
    getHarnessBooleanForLdStyleKey,
  };
}
